'use client';

import { useState, useEffect, useCallback } from 'react';
import debounce from 'lodash.debounce';
import { liveCitiesSearch } from '@/app/actions/countries.actions';
import { FormField, FormItem, FormControl, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { MapPin } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { ScrollArea } from './ui/scroll-area';

export default function SearchAutocomplete({
  form,
  defaultId = '',
  defaultLabel = '',
  customClass = '',
}) {
  const [searchValue, setSearchValue] = useState(defaultLabel || '');
  const [cities, setCities] = useState([]);
  const [searching, setSearching] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(false);

  useEffect(() => {
    if (defaultId) {
      form.setValue('city', defaultId);
    }
  }, [defaultId, form]);

  useEffect(() => {
    const subscription = form.watch((values) => {
      if (!values.city) {
        setSearchValue('');
        setCities([]);
        setOpenDropdown(false);
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const debouncedFetchCities = useCallback(
    debounce(async (query) => {
      if (!query.trim()) {
        setCities([]);
        return;
      }
      try {
        const data = await liveCitiesSearch(query);
        setCities(Array.isArray(data) ? data : []);
      } catch {
        setCities([]);
      } finally {
        setSearching(false);
      }
    }, 500),
    []
  );

  const handleInputChange = (event) => {
    const query = event.target.value;
    setSearchValue(query);

    if (query.length >= 1) {
      setSearching(true);
      setOpenDropdown(true);
      if (query.length >= 3) {
        debouncedFetchCities(query);
      }
    } else {
      setCities([]);
      setSearching(false);
      if (query.length === 0) {
      }
    }

    form.setValue('city', query);
  };

  return (
    <FormField
      control={form.control}
      name="city"
      render={() => (
        <FormItem>
          <div className="relative">
            <MapPin className="absolute top-1/2 left-3 z-10 h-5 w-5 -translate-y-1/2 transform text-black" />
            <FormControl>
              <Input
                type="text"
                placeholder="Search your city"
                value={searchValue}
                onChange={handleInputChange}
                // onBlur={() => setTimeout(() => setCities([]), 150)}
                autoComplete="off"
                className={
                  customClass ? customClass : 'h-12 rounded-xl border-gray-200 bg-gray-50 pl-10'
                }
              />
            </FormControl>

            <Popover open={openDropdown} onOpenChange={setOpenDropdown}>
              <PopoverTrigger asChild>
                <div />
              </PopoverTrigger>

              <PopoverContent
                align="start"
                sideOffset={8}
                className="mx-auto w-xs rounded-sm border-0 bg-white p-0 shadow-lg sm:w-[420px]"
                onOpenAutoFocus={(e) => e.preventDefault()}
              >
                <ScrollArea
                  className={`${cities.length <= 6 ? 'h-max' : 'h-[150px] sm:h-[250px]'}`}
                >
                  <>
                    {searchValue &&
                      !searching &&
                      cities.length > 0 &&
                      cities.map((item, index) => (
                        <div
                          key={item._id}
                          className="text-primary flex cursor-pointer justify-start gap-2 px-4 py-2 hover:bg-gray-200"
                          onClick={() => {
                            form.setValue('city', item._id);
                            form.setValue(
                              'cityTitle',
                              `${item.city}, ${item.state}, ${item.country}`
                            );
                            setSearchValue(`${item.city}, ${item.state}, ${item.country}`);
                            setCities([]);
                            setOpenDropdown(false);
                          }}
                        >
                          <div className="flex items-center gap-1">
                            <MapPin className="size-4" />
                            <div
                              className="line-clamp-2 text-sm font-semibold"
                              style={{ lineHeight: '18px' }}
                            >
                              {item.city},&nbsp;
                              {item.state},&nbsp;
                              {item.country},
                            </div>
                          </div>
                        </div>
                      ))}

                    {searchValue.length >= 3 && !searching && cities.length === 0 && (
                      <div className="flex cursor-pointer justify-start gap-2 px-4 py-2 text-gray-500 hover:bg-gray-200">
                        No city found. Try searching with different keywords.
                      </div>
                    )}

                    {searchValue.length < 3 && searching && (
                      <div className="flex cursor-pointer justify-start gap-2 px-4 py-2 text-gray-500 hover:bg-gray-200">
                        Please type at least three characters.
                      </div>
                    )}

                    {searchValue && searching && searchValue.length >= 3 && (
                      <div className="space-y-3 px-4 py-2">
                        {[...Array(6)].map((_, i) => (
                          <div key={i} className="flex items-center gap-3">
                            <div className="h-7 w-7 animate-pulse rounded bg-gray-200" />
                            <div className="h-7 w-full animate-pulse rounded bg-gray-200" />
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                </ScrollArea>
              </PopoverContent>
            </Popover>
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
